import React from 'react';
import { format } from 'date-fns';
import { Calendar, CheckCircle, ChevronDown, ChevronUp, Flag, Play, Zap } from 'lucide-react';
import { toast } from 'sonner';
import TaskForm from './TaskForm';
import type { Task } from '../../lib/db';
import type { LocalTimerApi } from '../../hooks/useLocalTimer';

interface TaskListMobileProps {
  tasks: Task[];
  expandedTask: string | null;
  onToggleExpand: (taskId: string) => void;
  localTimer: LocalTimerApi;
  onStartTimeEntry: (task: Task) => void;
  onTaskUpdate: (taskId: string, updates: Partial<Task>) => Promise<void>;
  onTaskDelete: (taskId: string) => Promise<void>;
  onTaskReactivate: (taskId: string) => Promise<void>;
}

/**
 * Compact mobile-friendly list. Every tile expands to show quick actions and the edit form.
 */
export function TaskListMobile({
  tasks,
  expandedTask,
  onToggleExpand,
  localTimer,
  onStartTimeEntry,
  onTaskUpdate,
  onTaskDelete,
  onTaskReactivate
}: TaskListMobileProps) {
  if (!tasks.length) {
    return null;
  }

  return (
    <div className="md:hidden space-y-2">
      {tasks.map((task) => {
        const isExpanded = expandedTask === task.id;
        const isComplete = task.status === 'Complete';
        const isOverdue = task.deadline && new Date(task.deadline) < new Date() && !isComplete;

        return (
          <div key={task.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div
              className={`p-4 ${isComplete ? 'bg-green-50' : isOverdue ? 'bg-red-50' : ''}`}
              onClick={() => onToggleExpand(task.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isComplete ? 'line-through text-gray-500' : ''}`}>
                      {task.title}
                    </span>
                    <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full ${
                      task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                      task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                      task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {task.priority || 'Low'}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      task.task_type === 'Done' ? 'bg-green-100 text-green-800' :
                      task.task_type === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                      task.task_type === 'Waiting' ? 'bg-yellow-100 text-yellow-800' :
                      task.task_type === 'Chasing' ? 'bg-orange-100 text-orange-800' :
                      task.task_type === 'Parked' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {task.task_type || 'To Do'}
                    </span>

                    {task.is_live && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                        Live
                      </span>
                    )}

                    {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                      <span
                        className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 rounded-full"
                        title="We bumped this task, so it will return to Focus Mode tomorrow"
                      >
                        Bumped · Back Tomorrow
                      </span>
                    )}

                    <div className="flex items-center gap-1">
                      <Flag className="w-4 h-4" />
                      <span className={task.status === 'Complete' ? 'text-green-600 font-medium' : ''}>
                        {task.status || 'To Do'}
                      </span>
                    </div>

                    {task.deadline && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                          {format(new Date(task.deadline), 'MMM d')}
                        </span>
                      </div>
                    )}

                    {isComplete && task.end_date && (
                      <div className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        <span>Completed</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {task.status !== 'Complete' && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!localTimer.isTimerRunningForTask(task.id)) {
                          onStartTimeEntry(task);
                        }
                      }}
                      className={`p-2 rounded-lg transition-all ${
                        localTimer.isTimerRunningForTask(task.id)
                          ? 'bg-green-100 text-green-600 cursor-default'
                          : 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200 active:scale-95'
                      }`}
                      title={localTimer.isTimerRunningForTask(task.id) ? 'Timer running' : 'Start Timer'}
                      disabled={localTimer.isTimerRunningForTask(task.id)}
                    >
                      <Play className="w-5 h-5" />
                    </button>
                  )}

                  {task.status !== 'Complete' && !task.is_live && (
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        onTaskUpdate(task.id, { is_live: true }).catch((error) => {
                          console.error('Error making task live:', error);
                          toast.error('Failed to make task live');
                        });
                      }}
                      className="p-2 bg-green-100 text-green-600 hover:bg-green-200 rounded-lg transition-all active:scale-95"
                      title="Make Task Live"
                    >
                      <Zap className="w-5 h-5" />
                    </button>
                  )}

                  <button className="p-1" onClick={(event) => {
                    event.stopPropagation();
                    onToggleExpand(task.id);
                  }}>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <TaskForm
                  task={task}
                  onSubmit={async (updates) => {
                    try {
                      await onTaskUpdate(task.id, updates);
                      onToggleExpand(task.id);
                    } catch (error) {
                      console.error('Error updating task:', error);
                      toast.error('Failed to update task. Please try again.');
                    }
                  }}
                  onDelete={async () => {
                    await onTaskDelete(task.id);
                    onToggleExpand(task.id);
                  }}
                  onCancel={() => onToggleExpand(task.id)}
                  availableTasks={tasks}
                />

                {task.notes && task.notes.includes('[Deferred to Tomorrow]') && (
                  <button
                    onClick={async () => {
                      await onTaskReactivate(task.id);
                    }}
                    className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-100 text-orange-600 hover:bg-orange-200 rounded-lg transition-colors"
                  >
                    Bring back now
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

